import { useState, useEffect } from 'react';

interface SmartSuggestionsProps {
  darkMode: boolean;
  selectedStyle: string;
  floorPlanConfig: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    unit: string;
    kitchenType: string;
    extras: string[];
  };
  onApplySuggestion?: (config: Partial<any>) => void;
}

interface RoomSuggestion {
  id: string;
  room: string;
  icon: string;
  suggestion: string;
  reason: string;
  tag: 'Recommended' | 'Popular' | 'Trending' | 'Optimal';
  tagColor: string;
}

interface LayoutPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  bedrooms: number;
  bathrooms: number;
  kitchenType: 'open' | 'closed';
  extras: string[];
  bestFor: string;
  imageUrl: string;
}

const getLayoutPresets = (style: string, area: number, unit: string): LayoutPreset[] => {
  const sqm = unit === 'sqft' ? Math.round(area * 0.0929) : area;

  const presets: LayoutPreset[] = [
    {
      id: 'open-concept',
      name: 'Open Concept Living',
      description: 'Merged living, dining and kitchen for a spacious, social atmosphere',
      icon: 'ri-layout-masonry-line',
      bedrooms: Math.min(Math.max(Math.floor(sqm / 30), 2), 4),
      bathrooms: 2,
      kitchenType: 'open',
      extras: ['Balcony', 'Laundry Room'],
      bestFor: 'Entertaining & families',
      imageUrl: `https://public.readdy.ai/ai/img_res/91bd3911183f3675471e075cb377e6fa.jpg$%7Bstyle%7D%20style%20architectural%20rendering%20professional%20visualization&width=400&height=280&seq=suggest-open-${style}&orientation=landscape`,
    },
    {
      id: 'split-bedroom',
      name: 'Split Bedroom Layout',
      description: 'Master suite on one side, secondary bedrooms on the other for privacy',
      icon: 'ri-layout-column-line',
      bedrooms: Math.min(Math.max(Math.floor(sqm / 28), 3), 5),
      bathrooms: 2,
      kitchenType: 'closed',
      extras: ['Home Office', 'Garage'],
      bestFor: 'Families with children',
      imageUrl: `https://readdy.ai/api/search-image?query=split%20bedroom%20floor%20plan%20layout%20master%20suite%20privacy%20modern%20house%20$%7Bstyle%7D%20style%20architectural%20rendering%20professional%20visualization&width=400&height=280&seq=suggest-split-${style}&orientation=landscape`,
    },
    {
      id: 'courtyard',
      name: 'Courtyard-Centered',
      description: 'Rooms arranged around a central outdoor courtyard for natural light',
      icon: 'ri-focus-3-line',
      bedrooms: Math.min(Math.max(Math.floor(sqm / 35), 2), 4),
      bathrooms: 2,
      kitchenType: 'open',
      extras: ['Garden', 'Terrace'],
      bestFor: 'Warm climates & privacy',
      imageUrl: `https://readdy.ai/api/search-image?query=courtyard%20house%20floor%20plan%20central%20garden%20natural%20light%20rooms%20surrounding%20outdoor%20space%20$%7Bstyle%7D%20style%20architectural%20rendering%20professional%20visualization&width=400&height=280&seq=suggest-court-${style}&orientation=landscape`,
    },
    {
      id: 'loft-style',
      name: 'Loft-Style Open Plan',
      description: 'Minimal walls, flexible zones defined by furniture and lighting',
      icon: 'ri-layout-grid-line',
      bedrooms: Math.min(Math.max(Math.floor(sqm / 40), 1), 3),
      bathrooms: 1,
      kitchenType: 'open',
      extras: ['Balcony', 'Home Office'],
      bestFor: 'Singles & couples',
      imageUrl: `https://readdy.ai/api/search-image?query=loft%20style%20open%20plan%20minimal%20walls%20flexible%20zones%20modern%20interior%20$%7Bstyle%7D%20style%20architectural%20rendering%20professional%20visualization&width=400&height=280&seq=suggest-loft-${style}&orientation=landscape`,
    },
  ];

  return presets;
};

const getRoomSuggestions = (style: string, area: number, unit: string, bedrooms: number, extras: string[]): RoomSuggestion[] => {
  const sqm = unit === 'sqft' ? Math.round(area * 0.0929) : area;
  const suggestions: RoomSuggestion[] = [];

  // Living Room
  const livingSize = Math.round(sqm * 0.18);
  suggestions.push({
    id: 'living',
    room: 'Living Room',
    icon: 'ri-sofa-line',
    suggestion: `${livingSize}–${livingSize + 5} m² with ${style === 'Minimalist' ? 'clean lines and minimal furniture' : style === 'Mediterranean' ? 'arched openings and warm tones' : 'open flow to dining area'}`,
    reason: `Based on your ${sqm}m² total area and ${style} style`,
    tag: 'Recommended',
    tagColor: 'bg-teal-100 text-teal-700',
  });

  // Master Bedroom
  const masterSize = Math.round(sqm * 0.14);
  suggestions.push({
    id: 'master',
    room: 'Master Bedroom',
    icon: 'ri-hotel-bed-line',
    suggestion: `${masterSize}–${masterSize + 4} m² with en-suite bathroom${sqm > 120 ? ' and walk-in closet' : ''}`,
    reason: `Optimal for ${bedrooms}-bedroom layout at this scale`,
    tag: 'Optimal',
    tagColor: 'bg-emerald-100 text-emerald-700',
  });

  // Kitchen
  suggestions.push({
    id: 'kitchen',
    room: 'Kitchen',
    icon: 'ri-knife-line',
    suggestion: `${style === 'Farmhouse' ? 'Large farmhouse kitchen with island and pantry' : style === 'Minimalist' ? 'Sleek galley kitchen with hidden storage' : 'L-shaped kitchen with breakfast bar'}, ~${Math.round(sqm * 0.1)}m²`,
    reason: `${style} style pairs best with this kitchen configuration`,
    tag: 'Popular',
    tagColor: 'bg-amber-100 text-amber-700',
  });

  // Bathroom
  suggestions.push({
    id: 'bathroom',
    room: 'Bathrooms',
    icon: 'ri-drop-line',
    suggestion: `1 full en-suite + ${bedrooms > 3 ? '2 shared bathrooms' : '1 shared bathroom'} with ${style === 'Luxury' || sqm > 150 ? 'freestanding tub and double vanity' : 'walk-in shower'}`,
    reason: `Recommended ratio for ${bedrooms} bedrooms`,
    tag: 'Recommended',
    tagColor: 'bg-teal-100 text-teal-700',
  });

  // Conditional extras
  if (extras.includes('Home Office') || sqm > 130) {
    suggestions.push({
      id: 'office',
      room: 'Home Office',
      icon: 'ri-computer-line',
      suggestion: `${Math.round(sqm * 0.07)}–${Math.round(sqm * 0.09)} m² near entrance or away from bedrooms for focus`,
      reason: 'Trending in post-2020 home designs',
      tag: 'Trending',
      tagColor: 'bg-violet-100 text-violet-700',
    });
  }

  if (extras.includes('Garage') || style === 'Colonial' || style === 'Farmhouse') {
    suggestions.push({
      id: 'garage',
      room: 'Garage',
      icon: 'ri-car-line',
      suggestion: `Double garage (~${Math.round(sqm * 0.12)}m²) with direct interior access and storage wall`,
      reason: `Common in ${style} style homes`,
      tag: 'Popular',
      tagColor: 'bg-amber-100 text-amber-700',
    });
  }

  return suggestions;
};

export default function SmartSuggestions({ darkMode, selectedStyle, floorPlanConfig, onApplySuggestion }: SmartSuggestionsProps) {
  const [activeSection, setActiveSection] = useState<'layouts' | 'rooms'>('layouts');
  const [appliedPreset, setAppliedPreset] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);

  const style = selectedStyle || 'Contemporary';
  const presets = getLayoutPresets(style, floorPlanConfig.area, floorPlanConfig.unit);
  const roomSuggestions = getRoomSuggestions(style, floorPlanConfig.area, floorPlanConfig.unit, floorPlanConfig.bedrooms, floorPlanConfig.extras);

  useEffect(() => {
    setAnalyzed(false);
  }, [selectedStyle, floorPlanConfig.area, floorPlanConfig.bedrooms]);

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      setAnalyzed(true);
    }, 1800);
  };

  const handleApplyPreset = (preset: LayoutPreset) => {
    setAppliedPreset(preset.id);
    if (onApplySuggestion) {
      onApplySuggestion({
        bedrooms: preset.bedrooms,
        bathrooms: preset.bathrooms,
        kitchenType: preset.kitchenType,
        extras: preset.extras,
      });
    }
    setToastMsg(`"${preset.name}" layout applied!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const card = darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="p-5 space-y-5">
      {/* Header */}
      <div>
        <h3 className={`text-base font-bold ${text} mb-1`}>Smart Suggestions</h3>
        <p className={`text-xs ${subtext}`}>
          AI-powered layout recommendations based on your <span className="font-semibold text-teal-500">{style}</span> style and <span className="font-semibold text-teal-500">{floorPlanConfig.area} {floorPlanConfig.unit}</span> area
        </p>
      </div>

      {/* Analyze Button */}
      {!analyzed && (
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
            isAnalyzing
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-teal-500/30'
          }`}
        >
          {isAnalyzing ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Analyzing your configuration...
            </>
          ) : (
            <>
              <i className="ri-brain-line"></i>
              Analyze &amp; Generate Suggestions
            </>
          )}
        </button>
      )}

      {/* Analyzing Progress */}
      {isAnalyzing && (
        <div className={`${darkMode ? 'bg-teal-900/20 border-teal-700' : 'bg-teal-50 border-teal-200'} border rounded-xl p-4`}>
          <div className="space-y-2">
            {['Reading style preferences...', 'Calculating optimal room ratios...', 'Matching layout patterns...', 'Generating recommendations...'].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <i className={`ri-loader-4-line text-xs text-teal-500 animate-spin`}></i>
                <span className={`text-xs ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content after analysis */}
      {analyzed && (
        <>
          {/* Re-analyze */}
          <button
            onClick={() => { setAnalyzed(false); }}
            className={`w-full py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap border ${
              darkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            <i className="ri-refresh-line"></i>
            Re-analyze with current settings
          </button>

          {/* Section Tabs */}
          <div className={`flex rounded-xl p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {(['layouts', 'rooms'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setActiveSection(s)}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                  activeSection === s
                    ? 'bg-teal-500 text-white shadow-sm'
                    : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {s === 'layouts' ? '🏠 Layout Presets' : '📐 Room Sizing'}
              </button>
            ))}
          </div>

          {/* Layout Presets */}
          {activeSection === 'layouts' && (
            <div className="space-y-4">
              <p className={`text-xs ${subtext}`}>
                {presets.length} layout presets matched for <strong>{style}</strong> style
              </p>
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    appliedPreset === preset.id
                      ? 'border-teal-500 ring-2 ring-teal-500/30'
                      : darkMode ? 'border-gray-600' : 'border-gray-200'
                  } ${card}`}
                >
                  {/* Image */}
                  <div className="w-full h-36 overflow-hidden relative">
                    <img
                      src={preset.imageUrl}
                      alt={preset.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-2 left-3">
                      <span className="text-white text-xs font-semibold">{preset.bestFor}</span>
                    </div>
                    {appliedPreset === preset.id && (
                      <div className="absolute top-2 right-2 w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-teal-50'}`}>
                          <i className={`${preset.icon} text-sm text-teal-500`}></i>
                        </div>
                        <h4 className={`text-sm font-bold ${text}`}>{preset.name}</h4>
                      </div>
                    </div>
                    <p className={`text-xs ${subtext} mb-3 leading-relaxed`}>{preset.description}</p>

                    {/* Stats */}
                    <div className="flex gap-2 mb-3 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {preset.bedrooms} bed
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {preset.bathrooms} bath
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'} capitalize`}>
                        {preset.kitchenType} kitchen
                      </span>
                    </div>

                    <button
                      onClick={() => handleApplyPreset(preset)}
                      className={`w-full py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        appliedPreset === preset.id
                          ? 'bg-teal-500 text-white'
                          : darkMode
                          ? 'bg-gray-600 hover:bg-teal-600 text-gray-200 hover:text-white'
                          : 'bg-gray-100 hover:bg-teal-500 text-gray-700 hover:text-white'
                      }`}
                    >
                      {appliedPreset === preset.id ? (
                        <><i className="ri-check-line mr-1"></i>Applied</>
                      ) : (
                        <><i className="ri-magic-line mr-1"></i>Apply This Layout</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Room Sizing */}
          {activeSection === 'rooms' && (
            <div className="space-y-3">
              <p className={`text-xs ${subtext}`}>
                Optimal room sizes for <strong>{floorPlanConfig.area} {floorPlanConfig.unit}</strong> · <strong>{style}</strong> style
              </p>
              {roomSuggestions.map((s) => (
                <div key={s.id} className={`border rounded-xl p-4 ${card}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${darkMode ? 'bg-gray-600' : 'bg-teal-50'}`}>
                      <i className={`${s.icon} text-base text-teal-500`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-sm font-bold ${text}`}>{s.room}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.tagColor}`}>{s.tag}</span>
                      </div>
                      <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1 leading-relaxed`}>{s.suggestion}</p>
                      <p className={`text-xs ${subtext} flex items-center gap-1`}>
                        <i className="ri-information-line"></i>
                        {s.reason}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pro Tip */}
              <div className={`rounded-xl p-4 border ${darkMode ? 'bg-teal-900/20 border-teal-700' : 'bg-teal-50 border-teal-200'}`}>
                <div className="flex items-start gap-2">
                  <i className="ri-lightbulb-line text-teal-500 text-base mt-0.5"></i>
                  <div>
                    <p className={`text-xs font-semibold ${darkMode ? 'text-teal-300' : 'text-teal-700'} mb-1`}>Pro Tip for {style} Style</p>
                    <p className={`text-xs ${darkMode ? 'text-teal-400' : 'text-teal-600'} leading-relaxed`}>
                      {style === 'Minimalist' && 'Use built-in storage to reduce visual clutter. Aim for 60% open floor space in living areas.'}
                      {style === 'Mediterranean' && 'Include a central courtyard or terrace. Arched doorways between rooms enhance the style.'}
                      {style === 'Farmhouse' && 'A large kitchen island is essential. Mudroom near the garage entry is highly recommended.'}
                      {style === 'Contemporary' && 'Floor-to-ceiling windows in living areas maximize natural light. Open-plan works best.'}
                      {style === 'Colonial' && 'Symmetrical room placement is key. Formal dining and living rooms should flank the entry.'}
                      {style === 'Victorian' && 'Include a parlor or sitting room. Bay windows add character and natural light.'}
                      {!['Minimalist','Mediterranean','Farmhouse','Contemporary','Colonial','Victorian'].includes(style) && `For ${style} homes, prioritize natural light and flow between main living spaces for the best result.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="text-sm font-medium">{toastMsg}</span>
        </div>
      )}
    </div>
  );
}
